import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import QRCode from 'qrcode';
import { ThemeContext } from '../components/Theme/ThemeContext';

const isRealLink = (link) => !!link && link !== '#';

export default function AppDownload() {
    const { isDark } = useContext(ThemeContext);
    const settings = useSelector(state => state.settingsdata.settings);
    const [qrDataUrl, setQrDataUrl] = useState(null);

    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isAndroid = /Android/i.test(ua);

    // Auto-redirect on mobile the moment the store links are available.
    useEffect(() => {
        if (!settings) return;
        if (isIOS && isRealLink(settings.AppleStoreLink)) {
            window.location.replace(settings.AppleStoreLink);
        } else if (isAndroid && isRealLink(settings.PlayStoreLink)) {
            window.location.replace(settings.PlayStoreLink);
        }
    }, [settings, isIOS, isAndroid]);

    // QR code encodes this same page's URL, so scanning it on any device
    // lands here and re-runs the same redirect logic for that device.
    useEffect(() => {
        QRCode.toDataURL(window.location.href, {
            margin: 1,
            width: 260,
            color: { dark: '#0A0A0B', light: '#ffffff' }
        }).then(setQrDataUrl).catch(() => {});
    }, []);

    const autoRedirecting = (isIOS && isRealLink(settings?.AppleStoreLink)) || (isAndroid && isRealLink(settings?.PlayStoreLink));

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            p: 3,
            backgroundColor: isDark ? '#08080a' : '#F8FAFC',
            color: isDark ? '#fff' : '#0F172A'
        }}>
            <Helmet>
                <title>Get the RAHL App</title>
            </Helmet>

            <Box
                component="img"
                src={require('../assets/img/Icon.png').default}
                alt="RAHL"
                sx={{ width: 72, height: 72, borderRadius: '18px', mb: 3 }}
            />

            {autoRedirecting ? (
                <>
                    <CircularProgress size={28} sx={{ color: '#E9AE31', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Taking you to the store...
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        If nothing happens, tap the button below.
                    </Typography>
                </>
            ) : (
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                    Get the RAHL App
                </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 4 }}>
                {isRealLink(settings?.AppleStoreLink) && (
                    <a href={settings.AppleStoreLink}>
                        <Box component="img" src={require('../assets/img/appstore.png').default} alt="Download on the App Store" sx={{ height: 52 }} />
                    </a>
                )}
                {isRealLink(settings?.PlayStoreLink) && (
                    <a href={settings.PlayStoreLink}>
                        <Box component="img" src={require('../assets/img/playstore.png').default} alt="Get it on Google Play" sx={{ height: 52 }} />
                    </a>
                )}
            </Box>

            {qrDataUrl && (
                <Box sx={{ mt: 5, p: 2, backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
                    <Box component="img" src={qrDataUrl} alt="Scan to download" sx={{ width: 180, height: 180, display: 'block' }} />
                </Box>
            )}
            <Typography variant="caption" sx={{ mt: 1.5, opacity: 0.6, maxWidth: 280 }}>
                Scan this code with your phone's camera to open this page and get redirected automatically.
            </Typography>
        </Box>
    );
}
